import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { PlatformService } from '@core/services/platform.service';
import { UrlHelperService } from '@core/services/url-helper.service';

@Injectable({
    providedIn: 'root'
})
export class MetaService {
    private preloadImage: HTMLLinkElement;
    private readonly originUrl: string;

    constructor(
        private title: Title,
        private meta: Meta,
        @Inject(DOCUMENT)
        private document: Document,
        private urlHelper: UrlHelperService,
        private platformService: PlatformService
    ) {
        this.originUrl = this.urlHelper.getOrigin();
    }

    public setHtmlLang(lang?: string): void {
        if (this.document?.documentElement && lang) {
            this.document.documentElement.lang = lang;
        }
    }

    public setTitle(title?: string): void {
        this.title.setTitle(title || '');
    }

    public setDescription(description?: string): void {
        this.meta.updateTag({ name: 'description', content: description || '' });
    }

    public setNoIndex(shouldBlock?: boolean): void {
        if (shouldBlock) {
            this.meta.updateTag({
                name: 'robots',
                content: 'noindex, nofollow'
            });
        } else {
            this.meta.removeTag('name="robots"');
        }
    }

    public setAuthor(author?: string): void {
        this.meta.updateTag({
            name: 'author',
            content: author || ''
        });
    }

    public setCopyright(copyright?: string): void {
        this.meta.updateTag({
            name: 'copyright',
            content: copyright || ''
        });
    }

    public setCanonical(href?: string): void {
        if (href) {
            if (href.indexOf('http') === -1) {
                href = this.originUrl + href;
            }
        } else {
            const currentHref = this.urlHelper.getCurrentUrl();
            href = currentHref.origin + currentHref.pathname;
        }

        this.setLinkTag('canonical', href);
    }

    /**
     * Use for biggest LCP on a page, hero images fx.
     * Adds a preload link to head, to make sure the image request happens early
     * @param src
     * @param srcSet
     * @param priority 'auto' | 'hight' - default is 'auto'
     */
    public setPreloadImage(src: string, srcSet: string, priority: 'auto' | 'high' = 'auto'): void {
        if (this.preloadImage || this.platformService.isBrowser) {
            return;
        }

        const link = this.document.createElement('link');
        if (srcSet) {
            link.setAttribute('imagesrcset', srcSet);
        }

        if (priority === 'high') {
            link.setAttribute('fetchPriority', priority);
        }

        link.setAttribute('as', 'image');
        link.rel = 'preload';
        link.href = src;

        this.preloadImage = this.document.getElementsByTagName('head')[0].appendChild(link);
    }

    private setLinkTag(rel: string, href?: string): void {
        let linkElement: HTMLLinkElement | undefined = this.document.querySelector(
            '[rel="' + rel + '"][href]'
        ) as HTMLLinkElement;
        if (linkElement) {
            linkElement.remove();
        }

        if (href) {
            linkElement = this.document.createElement('link');
            linkElement.setAttribute('rel', rel);
            linkElement.setAttribute('href', href);
            this.document.head.appendChild(linkElement);
        }
        linkElement = undefined;
    }
}
